import React, { useState, useMemo } from 'react';
import { FiSearch } from 'react-icons/fi';
import LocationDropdown from './LocationDropdown';
import { useTranslation } from "react-i18next";

const SearchFormForRent = ({
  properties,
  selectedLocations,
  setSelectedLocations,
  selectedType,
  setSelectedType,
  status,
  setStatus,
  rentPriceFilter,
  setRentPriceFilter,
  bedroomFilter,
  setBedroomFilter,
  setFilteredProperties,
  styles = {},
}) => {
  const { t } = useTranslation();

  // Controlled search text
  const [searchText, setSearchText] = useState("");

  const bedroomOptions = useMemo(() => [
    { label: t('bedrooms.1'), value: '1' },
    { label: t('bedrooms.2'), value: '2' },
    { label: t('bedrooms.3'), value: '3' },
    { label: t('bedrooms.4_plus'), value: '4+' },
  ], [t]);

  const [showBedroomDropdown, setShowBedroomDropdown] = useState(false);

  // สำหรับ slider ราคา
  const prices = properties?.map(p => Number(p.price)).filter(Boolean);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const [priceRange, setPriceRange] = useState([minPrice || 0, maxPrice || 1000000]);

  const handleSearchClick = () => {
    const filtered = properties.filter(p => {
      const matchText = searchText
        ? p.name?.toLowerCase().includes(searchText.toLowerCase()) || false
        : true;

      const matchLocation = selectedLocations.length > 0
        ? selectedLocations.includes(p.location)
        : true;

      const matchType = selectedType && selectedType !== 'all'
        ? p.type === selectedType
        : true;

      const matchStatus = status && status !== 'all'
        ? p.status === status
        : true;

      const matchPrice = Number(p.price) >= priceRange[0] && Number(p.price) <= priceRange[1];

      const matchBedroom = bedroomFilter
        ? bedroomFilter === '4+'
          ? p.bedrooms >= 4
          : p.bedrooms === Number(bedroomFilter)
        : true;

      // Scroll ไปยัง section เป้าหมาย
      const targetSection = document.getElementById('searchResults');
      if (targetSection) {
        targetSection.scrollIntoView({ behavior: 'smooth' });
      }

      return matchText && matchLocation && matchType && matchStatus && matchPrice && matchBedroom;
    });

    setFilteredProperties(filtered);
  };

  return (
    <div style={{ maxWidth: 700, margin: '20px auto' }}>
      {/* Filters row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: '1 1 220px', minWidth: 220, position: 'relative' }}>
          <LocationDropdown
            selectedLocations={selectedLocations}
            setSelectedLocations={setSelectedLocations}
          />
          <FiSearch style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#888', pointerEvents: 'none', fontSize: 18 }} />
        </div>

        {/* Type Dropdown */}
        <select value={selectedType} onChange={e => setSelectedType(e.target.value)}
          style={{ flex: '0 0 140px', padding: '10px 12px', borderRadius: 30, border: '1px solid #ccc', fontSize: 16, outline: 'none', fontFamily: "'Roboto Condensed', 'Kanit', sans-serif", cursor: 'pointer' }}>
          <option value="all">{t('All Types')}</option>
          <option value="Condo">{t('Condo')}</option>
          <option value="House">{t('House')}</option>
          <option value="Pool Villa/ Villa">{t('Villa')}</option>
          <option value="Land">{t('Land')}</option>
          <option value="Warehouse/Factory/Business">{t('Warehouse / Factory / Business')}</option>
          <option value="Commercial Building">{t('Commercial Building')}</option>
        </select>

        {/* Status Dropdown */}
        <select value={status} onChange={e => setStatus(e.target.value)}
          style={{ flex: '0 0 140px', padding: '10px 12px', borderRadius: 30, border: '1px solid #ccc', fontSize: 16, outline: 'none', cursor: 'pointer', fontFamily: "'Roboto Condensed', 'Kanit', sans-serif" }}>
          <option value="all">{t('All Statuses')}</option>
          <option value="Rent">{t('Rent')}</option>
        </select>
      </div>

      {/* Search Text Input */}
      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder={t('Search by property name')}
          style={{ width: '100%', padding: 10, borderRadius: 20, border: '1px solid #ccc', fontSize: 16, outline: 'none', fontFamily: "'Roboto Condensed', 'Kanit', sans-serif" }}
        />
      </div>

      {/* Rent Price Slider */}
      <div style={{
        border: '1px solid #ddd',
        borderRadius: 12,
        padding: 12,
        backgroundColor: '#fff',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        maxWidth: 300,
        marginBottom: 16
      }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', fontSize: 14 }}>
          {t('Price')}: {priceRange[0].toLocaleString()} - {priceRange[1].toLocaleString()} {t('THB/Month')}
        </label>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 12 }}>{t('Min')}</span>
          <input
            type="range"
            min={minPrice || 0}
            max={maxPrice || 100000}
            value={priceRange[0]}
            onChange={e => setPriceRange([Number(e.target.value), priceRange[1]])}
            style={{ flex: 1 }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12 }}>{t('Max')}</span>
          <input
            type="range"
            min={minPrice || 0}
            max={maxPrice || 100000}
            value={priceRange[1]}
            onChange={e => setPriceRange([priceRange[0], Number(e.target.value)])}
            style={{ flex: 1 }}
          />
        </div>
      </div>

      {/* Bedroom Dropdown */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', position: 'relative' }}>
        <div style={{ position: 'relative' }}>
          <button type="button" onClick={() => setShowBedroomDropdown(!showBedroomDropdown)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, border: '1px solid #ccc', backgroundColor: '#fff', fontSize: 14, cursor: 'pointer', color: bedroomFilter ? '#d10e0e' : '#333'
          }}>
            {t('Bedrooms')} {bedroomFilter ? `: ${bedroomOptions.find(b => b.value === bedroomFilter)?.label}` : ''}
          </button>
          {showBedroomDropdown && (
            <div style={{ position: 'absolute', top: '110%', left: 0, backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: 10, boxShadow: '0 4px 8px rgba(0,0,0,0.15)', padding: 10, zIndex: 1000, minWidth: 160 }}>
              {bedroomOptions.map(option => (
                <div key={option.value} onClick={() => { setBedroomFilter(option.value); setShowBedroomDropdown(false); }} style={{
                  padding: '6px 10px', cursor: 'pointer', backgroundColor: bedroomFilter === option.value ? '#d10e0e' : 'transparent', color: bedroomFilter === option.value ? '#fff' : '#333', borderRadius: 6, marginBottom: 6, userSelect: 'none'
                }}>
                  {option.label}
                </div>
              ))}
              <div onClick={() => setBedroomFilter(null)} style={{ padding: '6px 10px', cursor: 'pointer', color: '#888', borderTop: '1px solid #ddd', marginTop: 6, textAlign: 'center', userSelect: 'none' }}>
                {t('Clear')}
              </div>
            </div>
          )}
        </div>

        {/* Search Button */}
        <button
          onClick={handleSearchClick}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'center',
            backgroundColor: '#d10e0e',
            color: '#fff',
            border: 'none',
            borderRadius: 30,
            padding: '10px 28px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 3px 8px rgba(209,14,14,0.6)',
            fontSize: 16,
            marginTop: 12
          }}
        >
          {t('Search')}
        </button>

      </div>
    </div>
  );
};

export default SearchFormForRent;
